import { Pipe, PipeTransform } from '@angular/core';

// Garder synchronisé avec back/src/common/enums/basket-row-status.enum.ts (pas de source commune entre front/back)
export enum BasketRowStatusEnum {
  // Brouillon, line is added to a basket or adjustment is required
  DRAFT = 'DRAFT',
  // Attente de supervision, basket is validated by déclarant and supervision is required
  WAITING_SUPERVISION = 'WAITING_SUPERVISION',
  // Supervisé, line is validated and sub basket is API
  SUPERVISED = 'SUPERVISED',
  // Prise en Charge Frs, supplier confirmed handling of the sub basket or line
  SUPPLIER_HANDLED = 'SUPPLIER_HANDLED',
  // Rejet Frs, supplier refused the handling of the sub basket or line
  SUPPLIER_REJECTED = 'SUPPLIER_REJECTED',
  // Proposé, Specific to compétence type, supplier submitted an offer
  PROPOSITION = 'PROPOSITION',
  // Non Proposé, Specific to compétence type, no offer is possible for this line (API)
  NO_PROPOSITION = 'NO_PROPOSITION',
  // Attente d'autorisation, basket is validated and sub basket is not API OR API is done
  WAITING_AUTHORIZATION = 'WAITING_AUTHORIZATION',
  // Unauthorized, validator refused the spending
  UNAUTHORIZED = 'UNAUTHORIZED',
  // Réception théorique le, validator accepted the spending
  ORDERED = 'ORDERED',
  // Reliquat, line was partially delivered,
  BACKORDER = 'BACKORDER',
  // Reçu, the line was received correctly
  RECEIVED = 'RECEIVED',
  // Non Conforme, the linked reception was designated as non compliant
  NON_COMPLIANT = 'NON_COMPLIANT',
  // Indisponible, the contract linked to the ref is expired, loss of perimeter, suspension...
  UNAVAILABLE = 'UNAVAILABLE',
  // Mis à disposition
  AVAILABLE = 'AVAILABLE',
  // Remplacé, after an unavailability, the ref was replaced
  REPLACED = 'REPLACED',
  // Annulé, after an unavailability, the ref was cancelled
  CANCELLED = 'CANCELLED',
  // Abandonné, withdrawn by the déclarant
  ABANDONED = 'ABANDONED',
}

/**
 * Retourne la liste des statuts finaux.
 * AVAILABLE en est exclu : « mis à disposition » = en attente de retrait, donc pas
 * encore terminé. Miroir de getFinalBasketRowStatuses (back).
 */
export function getFinalBasketRowStatuses(): ReadonlyArray<BasketRowStatusEnum> {
  return [
    BasketRowStatusEnum.SUPPLIER_REJECTED,
    BasketRowStatusEnum.UNAUTHORIZED,
    BasketRowStatusEnum.RECEIVED,
    BasketRowStatusEnum.NON_COMPLIANT,
    BasketRowStatusEnum.REPLACED,
    BasketRowStatusEnum.CANCELLED,
    BasketRowStatusEnum.ABANDONED,
  ] as const;
}

/** Vrai si le statut est final */
export function isFinalBasketRowStatus(status: BasketRowStatusEnum): boolean {
  return getFinalBasketRowStatuses().includes(status);
}

/**
 * Statuts finaux exclus du périmètre d'autorisation (PRED-3097) : lignes affichées
 * à titre informatif (grisées, icône « Pour information ») et non comptabilisées
 * dans le résumé du sous-panier.
 */
export function getInformationalFinalStatuses(): ReadonlyArray<BasketRowStatusEnum> {
  return [
    BasketRowStatusEnum.SUPPLIER_REJECTED, // Rejet Frs
    BasketRowStatusEnum.UNAUTHORIZED, // Non autorisé
    BasketRowStatusEnum.CANCELLED, // Annulé
    BasketRowStatusEnum.REPLACED, // Remplacé
    BasketRowStatusEnum.ABANDONED, // Abandonné
    BasketRowStatusEnum.UNAVAILABLE, // Indisponible
  ] as const;
}

/** Vrai si la ligne est finale/hors périmètre d'autorisation (affichage informatif) */
export function isInformationalFinalStatus(status: BasketRowStatusEnum): boolean {
  return getInformationalFinalStatuses().includes(status);
}

/**
 * Statuts « intouchables » : statuts finaux + ORDERED.
 * ORDERED n'est plus un statut final (la ligne vit encore : BACKORDER, RECEIVED...),
 * mais une ligne commandée ne doit plus être modifiée ni déclencher d'alertes de
 * disponibilité / expiration : la commande est déjà partie chez le fournisseur.
 * Finalité (clôture du cycle de vie) → getFinalBasketRowStatuses.
 */
export function getUntouchableBasketRowStatuses(): ReadonlyArray<BasketRowStatusEnum> {
  return [...getFinalBasketRowStatuses(), BasketRowStatusEnum.ORDERED] as const;
}

/** Vrai si la ligne ne doit plus être modifiée ni déclencher d'alertes */
export function isUntouchableBasketRowStatus(status: BasketRowStatusEnum): boolean {
  return getUntouchableBasketRowStatuses().includes(status);
}

/**
 * Statuts spécifiques compétence (PRED-3107) dans lesquels une ligne attend l'arbitrage du
 * déclarant (accepter la proposition ou abandonner la ligne). Miroir du helper back
 * `getDeclarantArbitrationStatuses` (basket-row-status.enum.ts).
 */
export function getDeclarantArbitrationStatuses(): ReadonlyArray<BasketRowStatusEnum> {
  return [BasketRowStatusEnum.PROPOSITION, BasketRowStatusEnum.NO_PROPOSITION] as const;
}

/** Vrai si la ligne attend l'arbitrage du déclarant (proposition compétence) */
export function isAwaitingDeclarantArbitration(status: BasketRowStatusEnum): boolean {
  return getDeclarantArbitrationStatuses().includes(status);
}

@Pipe({ name: 'statusToText', pure: false })
export class StatusToTextPipe implements PipeTransform {
  transform(status: BasketRowStatusEnum): string {
    switch (status as string) {
      case BasketRowStatusEnum.DRAFT:
        return 'Brouillon';
      case BasketRowStatusEnum.WAITING_AUTHORIZATION:
        return 'En attente d’autorisation';
      case BasketRowStatusEnum.WAITING_SUPERVISION:
        return 'Supervision en cours';
      case BasketRowStatusEnum.SUPERVISED:
        return 'Supervisé';
      case BasketRowStatusEnum.SUPPLIER_HANDLED:
        return 'Prise en charge fournisseur';
      case BasketRowStatusEnum.PROPOSITION:
        return 'Proposé';
      case BasketRowStatusEnum.NO_PROPOSITION:
        return 'Non proposé';
      case BasketRowStatusEnum.AVAILABLE:
        return 'Mis à disposition';
      case BasketRowStatusEnum.ORDERED:
        return 'Commandé';
      case BasketRowStatusEnum.BACKORDER:
        return 'Reliquat';
      case BasketRowStatusEnum.RECEIVED:
        return 'Reçu';
      case BasketRowStatusEnum.REPLACED:
        return 'Remplacé';
      case BasketRowStatusEnum.CANCELLED:
        return 'Annulé';
      case BasketRowStatusEnum.UNAUTHORIZED:
        return 'Dépense non autorisée';
      case BasketRowStatusEnum.ABANDONED:
        return 'Abandonné';
      case BasketRowStatusEnum.UNAVAILABLE:
        return 'Indisponible';
      case BasketRowStatusEnum.NON_COMPLIANT:
        return 'Non conforme';
      case BasketRowStatusEnum.SUPPLIER_REJECTED:
        return 'Rejet fournisseur';
      // Legacy status
      case 'WAITING':
        return 'En attente';
      case 'CANCELED':
        return 'Annulé';
      case 'EXPIRED':
        return 'Expiré';
      case 'EXPIRED_ORDER_INTERRUPTED':
        return 'Engagement échu – Commande interrompue';
      case 'SUSPENDED':
        return 'Suspendu';
      case 'WAITING_APPRO':
        return ' ';
      case 'WAITING_REFERENT':
        return 'En attente référent engagement';
      case 'WAITING_CUSTOMER':
        return 'En attente déclarant';
      case 'WAITING_HC':
        return 'En attente hors catalogue';
      case 'HC_RETURN':
        return 'Retour hors catalogue';
      case 'CLOSED':
        return 'Clôturé';
      case 'CLOSED_BY_APPROVISIONER':
        return 'Référence annulée';
      case 'RECEIVED_ERROR':
        return 'Réception non conforme';
      case 'VALIDATED':
        return 'Dépense autorisée';
      default:
        return 'Inconnu';
    }
  }
}
